/**
 * Los dos programas de la consola (BB-2004 / BB-2022), como configuración y no como condicionales.
 *
 * `elegibles` es la consola de siempre: el universo pagable. `softKiller` es la pestaña nueva: viajes
 * que Data rechazó por un soft killer pero cuyo estado de pago ya es `BLOCKED_BY_CERTIFICATION`.
 * Pagables desde el 2026-10-09 (BB-2003; 38.851 viajes y 87.654,65 € ese día). Van en pestañas separadas, y no como un filtro más, por decisión de la 1:1 del
 * 2026-09-07: Ops no debe mezclar nunca ese universo con el pagable al decidir qué confirma.
 *
 * Un objeto por programa, y el panel (`PanelPrograma.tsx`) lo lee. Así la pantalla de soft killers
 * es la MISMA pantalla que la de elegibles, con otros filtros, otras distribuciones y otra callable
 * — no una copia que derive. Lo que cambia entre programas está aquí, y solo aquí.
 */

export type ProgramaId = 'elegibles' | 'softKiller';

/** Las distribuciones que el resumen puede pintar. Cada programa dice cuáles tienen sentido. */
export type DistribucionId =
  | 'ca'
  | 'miteco'
  | 'periodo'
  | 'fichero'
  | 'motivo'
  | 'origen'
  | 'blacklist'
  | 'pago';

export interface ProgramaConsola {
  id: ProgramaId;
  /** Texto de la pestaña. */
  pestana: string;
  /** Una frase bajo los filtros que dice qué universo se está mirando. */
  explicacion: string;
  /** Filtro de certificación MITECO (periodo | fichero). Solo la tabla de elegibles tiene esas columnas. */
  filtroCertificacion: boolean;
  /** Filtro por motivo de soft killer. Solo existe en ese programa. */
  filtroMotivo: boolean;
  /**
   * Filtro por conductor en blacklist (BB-2608). Las dos tablas tienen la columna, así que hoy va en
   * los dos; existe como opción por si un programa futuro no la tuviera (el backend la ignoraría).
   */
  filtroBlacklist: boolean;
  distribuciones: readonly DistribucionId[];
  /**
   * Si se PINTA el botón de confirmar. Es UX, NO seguridad: los gates de verdad viven en el backend
   * (`CONFIRMACION_HABILITADA`/`BACKEND_MODE` en `functions/.env` para elegibles; el rechazo
   * explícito de `/confirm` a `programa !== 'elegibles'` para soft killers). Este flag solo evita
   * el estado intermedio de un botón visible que el backend rechaza.
   */
  confirmacion: { habilitada: true } | { habilitada: false; motivo: string };
}

/**
 * El botón de confirmar de ELEGIBLES.
 *
 * Esto es UX, NO seguridad, y conviene no confundirlo: los gates de verdad son `BACKEND_MODE` y
 * `CONFIRMACION_HABILITADA` en `functions/.env`, que viven en el backend y no se pueden tocar desde
 * el cliente. Este flag solo decide si se PINTA el boton.
 *
 * Pasa a true en el mismo diff que esos dos, para que no exista el estado intermedio de un boton
 * visible que el backend rechaza, ni el de un backend que paga sin que se pueda pulsar.
 *
 * Historia, porque se ha movido cuatro veces y cada vez por una razón:
 *  - false (2026-08-11): un deploy ajeno horneo BACKEND_MODE=real sin que nadie lo decidiera ese dia.
 *  - true (2026-08-12): decision explicita de Javi para una prueba real (24 viajes, 57,70 EUR).
 *  - false el mismo dia, hecha la prueba: demo a Ops en la que nadie debia poder pagar.
 *  - TRUE desde el 2026-08-13, para quedarse: Javi y Ruben de vacaciones y la consola operativa para
 *    Ops. Decision explicita, descartando dejarla apagada.
 *
 * Dos cosas que este flag NO hace, y conviene no confundirlas:
 *  - No autoriza a nadie: eso es `requireClaim` en el backend, y el claim `caes_console` es BINARIO.
 *    Con esto encendido puede confirmar TODA la lista nombrada, no solo quien lo decidio. Cuantas
 *    personas son no se escribe aqui: `npm run acceso -- --listar` es la unica fuente.
 *  - No es el kill-switch, y apagar son DOS cosas. `CONFIRMACION_HABILITADA` en `functions/.env`
 *    mas redesplegar TODAS las functions detiene los pagos NUEVOS, pero desde BB-1667 no vacia lo
 *    ya encolado -- eso es `gcloud tasks queues pause caes-confirm-chunks --location=europe-west1`.
 *
 * Y sigue abierto: la 2SV de Workspace nunca se ha verificado.
 */
const CONFIRMACION_ELEGIBLES_HABILITADA = true;

/**
 * Marca que `tools/verify-deployment.sh` busca en el bundle SERVIDO para saber si el botón de
 * elegibles está compilado como armado o apagado. Antes el script buscaba un texto de la UI, pero
 * desde BB-2004 los dos textos (armado y apagado) están siempre en el bundle, porque la rama se
 * decide en ejecución con la config del programa — así que un texto ya no distingue nada. Esto es
 * una expresión constante: el minificador la pliega y solo UNA de las dos cadenas sobrevive. Va al
 * DOM (atributo `data-` en App.tsx) para que nadie la elimine por no usarse.
 */
export const MARCA_CONFIRMACION_ELEGIBLES = CONFIRMACION_ELEGIBLES_HABILITADA
  ? 'caes-front-confirmacion-elegibles:armada'
  : 'caes-front-confirmacion-elegibles:apagada';

/**
 * El botón de confirmar de SOFT KILLERS: armado desde el 2026-10-09 (BB-2003, decisión de Rubén con
 * Ops). Pagar uno es anular a sabiendas el veredicto de Data.
 *
 * Se mueve en el MISMO diff que el guard del backend (`esConfirmable` en `queryBuilder.ts`, que
 * `/confirm` consulta): un botón visible que el backend rechaza, o un backend que acepta sin botón,
 * son estados intermedios que no deben existir. Con el flag de elegibles y `functions/.env`, igual.
 */
const CONFIRMACION_SOFT_KILLER_HABILITADA = true;

export const PROGRAMA_ELEGIBLES: ProgramaConsola = {
  id: 'elegibles',
  pestana: 'Elegibles',
  explicacion:
    'Viajes elegibles CAE pendientes de certificar: el universo que la consola paga. Sin filtros se ve todo lo pendiente.',
  filtroCertificacion: true,
  filtroMotivo: false,
  filtroBlacklist: true,
  distribuciones: ['ca', 'miteco', 'periodo', 'fichero', 'blacklist', 'pago'],
  confirmacion: CONFIRMACION_ELEGIBLES_HABILITADA
    ? { habilitada: true }
    : {
        habilitada: false,
        motivo:
          'Esta version es de consulta: los datos son reales, pero confirmar el pago no esta disponible.',
      },
};

export const PROGRAMA_SOFT_KILLER: ProgramaConsola = {
  id: 'softKiller',
  pestana: 'Soft killers',
  explicacion:
    'Viajes que Data rechazo por un soft killer (limites, F1 o QA recuperable) y que ya estan en estado de pago bloqueado por certificacion. Pagarlos anula el rechazo de Data: acota con los filtros, sobre todo por motivo.',
  filtroCertificacion: false,
  filtroMotivo: true,
  filtroBlacklist: true,
  distribuciones: ['ca', 'miteco', 'motivo', 'origen', 'blacklist', 'pago'],
  confirmacion: CONFIRMACION_SOFT_KILLER_HABILITADA
    ? { habilitada: true }
    : {
        habilitada: false,
        motivo:
          'Confirmar soft killers no esta disponible en esta version.',
      },
};

export const PROGRAMAS: readonly ProgramaConsola[] = [PROGRAMA_ELEGIBLES, PROGRAMA_SOFT_KILLER];

/** Cómo se nombra un programa fuera de su pestaña (en el registro, por ejemplo). */
export function nombreDePrograma(id: ProgramaId | undefined): string {
  return id === 'softKiller' ? PROGRAMA_SOFT_KILLER.pestana : PROGRAMA_ELEGIBLES.pestana;
}
